import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import OpsPlanSectionTabs, { OPS_PLAN_TAB_DASHBOARD } from "../components/OpsPlanSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import OperatingPlanFyStatsRow from "../components/OperatingPlanFyStatsRow";
import {
  AOP_PCT_LEGEND_ITEMS,
  getAopActualPlanPctCellStyle,
} from "../shared/utils/aopActualPlanPctStyle";
import { buildAopActualDrillPathForCell } from "../shared/utils/aopActualEventsDrill";
import { canonicalizeFiscalYear } from "../shared/utils/fiscalYearMatch";
import {
  OPS_PLAN_AOP_PROGRESS_SUBTITLE,
  OPS_PLAN_LABEL_ACTUAL_HRS,
  OPS_PLAN_LABEL_ACTUAL_PLAN_PCT,
  OPS_PLAN_LABEL_PLAN_HRS,
  OPS_PLAN_LABEL_YEAR_PLAN_HRS,
  formatOpsPlanHrs,
} from "../shared/utils/opsPlanLabels";
import { AopProgressBarChart } from "./OperatingPlan";

import { apiFetch } from "../shared/api/apiFetch";

const dayjs = require("dayjs");
const uiConfig = require("../uiConfig");

const BREAKDOWN_SUMMARY = "summary";
const BREAKDOWN_WEEKLY = "weekly";
const BREAKDOWN_MONTHLY = "monthly";

function formatPct(value) {
  return value != null ? `${Number(value).toFixed(2)}%` : "—";
}

const thRightStyle = {
  textAlign: "right",
  padding: "8px 12px",
  fontWeight: 600,
  whiteSpace: "nowrap",
};

const tdRightStyle = {
  textAlign: "right",
  padding: "8px 12px",
  whiteSpace: "nowrap",
};

const thBucketStyle = {
  textAlign: "center",
  padding: "8px 12px",
  fontWeight: 600,
  backgroundColor: "#e7f1ff",
  borderBottom: "1px solid #dee2e6",
  whiteSpace: "nowrap",
};

const thSubStyle = {
  textAlign: "right",
  padding: "6px 12px",
  fontWeight: 600,
  fontSize: "12px",
  backgroundColor: "#f8f9fa",
  borderBottom: "2px solid #dee2e6",
  whiteSpace: "nowrap",
};

const thSubFirstInBucketStyle = {
  ...thSubStyle,
  borderLeft: "1px solid #dee2e6",
};

const tdBucketFirstStyle = {
  ...tdRightStyle,
  borderLeft: "1px solid #dee2e6",
};

function AopControlGroup({ label, children }) {
  return (
    <div
      className="toolbar-filter-group"
      style={{
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        padding: "5px 12px",
        background: "#fff",
        gap: "10px",
      }}
    >
      <span className="toolbar-label">{label}:</span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        {children}
      </span>
    </div>
  );
}

const aopRadioLabelStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  cursor: "pointer",
  fontSize: "13px",
  color: "#212529",
  margin: 0,
};

function getAopTableBuckets(breakdown, periods) {
  const ytdBucket = { key: "ytd", bucketLabel: "YTD", label: "YTD" };
  if (breakdown === BREAKDOWN_SUMMARY) {
    return [ytdBucket];
  }
  return [ytdBucket, ...periods];
}

function roundOpsPlan2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

function computeActualPlanPct(actual, plan) {
  return plan > 0 ? Math.round((actual / plan) * 10000) / 100 : null;
}

function enrichBreakdownRowWithYtd(row, ytdPlanFactor) {
  const ytdPlan =
    row.ytdPlan != null
      ? roundOpsPlan2(row.ytdPlan)
      : roundOpsPlan2(Number(row.yearPlan || 0) * (ytdPlanFactor ?? 0));
  const ytdActual = roundOpsPlan2(Number(row.ytdActual) || 0);
  const ytdActualEntered = roundOpsPlan2(Number(row.ytdActualEntered) || 0);
  const ytdActualEvents = roundOpsPlan2(Number(row.ytdActualEvents) || 0);
  return {
    ...row,
    ytdPlan,
    ytdActual,
    ytdActualEntered,
    ytdActualEvents,
    ytdActualPlanPct:
      row.ytdActualPlanPct != null
        ? row.ytdActualPlanPct
        : computeActualPlanPct(ytdActual, ytdPlan),
  };
}

function buildYtdSummaryTotals(rows) {
  const totalYearPlan = roundOpsPlan2(
    rows.reduce((s, r) => s + (Number(r.yearPlan) || 0), 0)
  );
  const totalYtdPlan = roundOpsPlan2(
    rows.reduce((s, r) => s + (Number(r.ytdPlan) || 0), 0)
  );
  const totalYtdActual = roundOpsPlan2(
    rows.reduce((s, r) => s + (Number(r.ytdActual) || 0), 0)
  );
  const totalYtdActualEntered = roundOpsPlan2(
    rows.reduce((s, r) => s + (Number(r.ytdActualEntered) || 0), 0)
  );
  const totalYtdActualEvents = roundOpsPlan2(
    rows.reduce((s, r) => s + (Number(r.ytdActualEvents) || 0), 0)
  );
  return {
    totalYearPlan,
    totalYtdPlan,
    totalYtdActual,
    totalYtdActualEntered,
    totalYtdActualEvents,
    totalYtdActualPlanPct: computeActualPlanPct(totalYtdActual, totalYtdPlan),
  };
}

const aopActualDrillLinkStyle = {
  color: "#0d6efd",
  textDecoration: "none",
};

function AopProgressMetricCells({ values, actualDrillPath }) {
  const actualFormatted = formatOpsPlanHrs(values.actual);
  const actualEvents = Number(values.actualEvents ?? 0);
  const actualEntered = Number(values.actualEntered ?? 0);
  const showActualDrillLink = actualDrillPath && actualEvents > 0;
  const cellTitle =
    actualEntered > 0
      ? `Entered ${formatOpsPlanHrs(actualEntered)} + events ${formatOpsPlanHrs(actualEvents)}`
      : actualEvents > 0
        ? "View closed events contributing to event actual"
        : undefined;

  return (
    <>
      <td style={tdBucketFirstStyle}>{formatOpsPlanHrs(values.plan)}</td>
      <td style={tdRightStyle} title={cellTitle}>
        {showActualDrillLink ? (
          <Link
            to={actualDrillPath}
            style={aopActualDrillLinkStyle}
            title={cellTitle || "View closed events contributing to this actual"}
          >
            {actualFormatted}
          </Link>
        ) : (
          actualFormatted
        )}
      </td>
      <td style={getAopActualPlanPctCellStyle(values.actualPlanPct, tdRightStyle)}>
        {formatPct(values.actualPlanPct)}
      </td>
    </>
  );
}

function AopPerformanceLegend() {
  return (
    <div
      style={{
        border: "1px solid #dee2e6",
        borderRadius: "8px",
        padding: "10px 12px",
        background: "#fff",
        fontSize: "12px",
        flexShrink: 0,
        minWidth: "210px",
        alignSelf: "flex-start",
      }}
    >
      <div
        style={{
          fontWeight: 600,
          marginBottom: "8px",
          color: "#495057",
          fontSize: "13px",
        }}
      >
        Performance Legend:
      </div>
      {AOP_PCT_LEGEND_ITEMS.map((item) => (
        <div
          key={item.key}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "6px",
            color: "#212529",
          }}
        >
          <span
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: item.color,
              borderRadius: "2px",
              flexShrink: 0,
            }}
          />
          <span>
            <strong>{item.label}</strong> ({item.range})
          </span>
        </div>
      ))}
    </div>
  );
}

function getAopBucketHeaderLabel(bucket) {
  return bucket.label || bucket.bucketLabel;
}

function AopProgressTableHead({ breakdown, periods }) {
  const buckets = getAopTableBuckets(breakdown, periods);
  const stickyThStyle = {
    textAlign: "left",
    padding: "8px 12px",
    fontWeight: 600,
    position: "sticky",
    left: 0,
    background: "#fff",
    zIndex: 2,
    verticalAlign: "bottom",
    borderBottom: "2px solid #dee2e6",
  };

  return (
    <thead>
      <tr>
        <th rowSpan={2} style={stickyThStyle}>
          Program
        </th>
        <th
          rowSpan={2}
          style={{
            ...thRightStyle,
            verticalAlign: "bottom",
            borderBottom: "2px solid #dee2e6",
          }}
        >
          {OPS_PLAN_LABEL_YEAR_PLAN_HRS}
        </th>
        {buckets.map((bucket) => {
          const headerLabel = getAopBucketHeaderLabel(bucket);
          const headerTitle =
            bucket.title && bucket.title !== headerLabel ? bucket.title : undefined;
          return (
            <th
              key={bucket.key}
              colSpan={3}
              style={thBucketStyle}
              title={headerTitle}
            >
              {headerLabel}
            </th>
          );
        })}
      </tr>
      <tr>
        {buckets.map((bucket) => (
          <React.Fragment key={`${bucket.key}-sub`}>
            <th style={thSubFirstInBucketStyle}>{OPS_PLAN_LABEL_PLAN_HRS}</th>
            <th style={thSubStyle}>{OPS_PLAN_LABEL_ACTUAL_HRS}</th>
            <th style={thSubStyle}>{OPS_PLAN_LABEL_ACTUAL_PLAN_PCT}</th>
          </React.Fragment>
        ))}
      </tr>
    </thead>
  );
}

function AopProgressDataTable({
  breakdown,
  periods,
  rows,
  totalYearPlan,
  totalPeriods,
  ytdPlanFactor,
  buildActualDrillPath,
  showLegend = false,
}) {
  const displayRows =
    breakdown === BREAKDOWN_SUMMARY
      ? rows
      : rows.map((row) => enrichBreakdownRowWithYtd(row, ytdPlanFactor));
  const ytdTotals = buildYtdSummaryTotals(displayRows);

  function renderYtdCells(values, drillArgs) {
    return (
      <AopProgressMetricCells
        values={values}
        actualDrillPath={buildActualDrillPath({
          ...drillArgs,
          breakdownOverride: BREAKDOWN_SUMMARY,
        })}
      />
    );
  }

  function renderPeriodCells(row, programName) {
    return (row.periods || []).map((p, idx) => (
      <AopProgressMetricCells
        key={`${programName}-${periods[idx]?.key || idx}`}
        values={{
          plan: p.plan,
          actual: p.actual,
          actualEntered: 0,
          actualEvents: p.actualEvents ?? p.actual,
          actualPlanPct: p.actualPlanPct,
        }}
        actualDrillPath={buildActualDrillPath({
          programName,
          period: periods[idx],
        })}
      />
    ));
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "16px",
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          flex: "1 1 0",
          minWidth: 0,
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          overflowX: "auto",
        }}
      >
        <table
          style={{
            width: "auto",
            minWidth: "min-content",
            borderCollapse: "collapse",
            fontSize: "13px",
          }}
        >
          <AopProgressTableHead breakdown={breakdown} periods={periods} />
          <tbody>
            {displayRows.map((r) => (
              <tr key={r.program} style={{ borderBottom: "1px solid #eee" }}>
                <td
                  style={{
                    padding: "8px 12px",
                    position: "sticky",
                    left: 0,
                    background: "#fff",
                    zIndex: 1,
                  }}
                >
                  {r.program}
                </td>
                <td style={tdRightStyle}>{formatOpsPlanHrs(r.yearPlan)}</td>
                {renderYtdCells(
                  {
                    plan: r.ytdPlan,
                    actual: r.ytdActual,
                    actualEntered: r.ytdActualEntered,
                    actualEvents: r.ytdActualEvents,
                    actualPlanPct: r.ytdActualPlanPct,
                  },
                  { programName: r.program }
                )}
                {breakdown !== BREAKDOWN_SUMMARY
                  ? renderPeriodCells(r, r.program)
                  : null}
              </tr>
            ))}
            <tr className="table-row-total">
              <td
                style={{
                  padding: "8px 12px",
                  position: "sticky",
                  left: 0,
                  background: "#f8f9fa",
                  zIndex: 1,
                }}
              >
                Total
              </td>
              <td style={tdRightStyle}>{formatOpsPlanHrs(totalYearPlan)}</td>
              {renderYtdCells(
                {
                  plan: ytdTotals.totalYtdPlan,
                  actual: ytdTotals.totalYtdActual,
                  actualEntered: ytdTotals.totalYtdActualEntered,
                  actualEvents: ytdTotals.totalYtdActualEvents,
                  actualPlanPct: ytdTotals.totalYtdActualPlanPct,
                },
                { isTotalRow: true }
              )}
              {breakdown !== BREAKDOWN_SUMMARY
                ? (totalPeriods || []).map((p, idx) => (
                    <AopProgressMetricCells
                      key={`total-${periods[idx]?.key || idx}`}
                      values={p}
                      actualDrillPath={buildActualDrillPath({
                        isTotalRow: true,
                        period: periods[idx],
                      })}
                    />
                  ))
                : null}
            </tr>
          </tbody>
        </table>
      </div>
      {showLegend ? <AopPerformanceLegend /> : null}
    </div>
  );
}

function OpsPlanDashboard() {
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
  const account_id = userInfo.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [currentFyStartDate, setCurrentFyStartDate] = useState(() =>
    dayjs().startOf("year").format("YYYY-MM-DD")
  );
  const [currentFy, setCurrentFy] = useState(() => String(dayjs().year()));
  const [aopProgressData, setAopProgressData] = useState({
    breakdown: BREAKDOWN_SUMMARY,
    rows: [],
    periods: [],
    daysElapsed: 0,
    totalYearPlan: 0,
    totalYtdPlan: 0,
    totalYtdPlanPct: null,
    totalYtdActual: 0,
    totalYtdActualEntered: 0,
    totalYtdActualEvents: 0,
    totalYtdActualPlanPct: null,
    totalPeriods: [],
  });
  const [aopProgressViewMode, setAopProgressViewMode] = useState("chart");
  const [aopProgressBreakdown, setAopProgressBreakdown] = useState(BREAKDOWN_SUMMARY);
  // Current AOP Progress: program filter ("" = All Programs)
  const [aopProgressProgramFilter, setAopProgressProgramFilter] = useState("");
  const [programs, setPrograms] = useState([]);
  const [activeTab, setActiveTab] = useState("current");
  const asOfDateYmd = dayjs().format("YYYY-MM-DD");

  function fetchAopProgress(breakdownOverride) {
    if (!account_id) return;
    const breakdown = breakdownOverride || aopProgressBreakdown;
    apiFetch(serverUrl + "/getOperatingPlanAopProgress", {
      method: "POST",
      body: JSON.stringify({
        account_id,
        asOfDate: dayjs().format("YYYY-MM-DD"),
        breakdown,
      }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (data && Array.isArray(data.rows)) {
          setAopProgressData({
            breakdown: data.breakdown || BREAKDOWN_SUMMARY,
            rows: data.rows,
            periods: Array.isArray(data.periods) ? data.periods : [],
            daysElapsed: Number(data.daysElapsed) || 0,
            totalYearPlan: data.totalYearPlan ?? 0,
            totalYtdPlan: data.totalYtdPlan ?? 0,
            totalYtdPlanPct: data.totalYtdPlanPct ?? null,
            totalYtdActual: data.totalYtdActual ?? 0,
            totalYtdActualEntered: data.totalYtdActualEntered ?? 0,
            totalYtdActualEvents: data.totalYtdActualEvents ?? 0,
            totalYtdActualPlanPct: data.totalYtdActualPlanPct ?? null,
            totalPeriods: Array.isArray(data.totalPeriods) ? data.totalPeriods : [],
          });
        }
      })
      .catch(() => setAopProgressData((prev) => ({ ...prev, rows: [] })));
  }

  function handleBreakdownChange(nextBreakdown) {
    setAopProgressBreakdown(nextBreakdown);
    if (nextBreakdown !== BREAKDOWN_SUMMARY) {
      setAopProgressViewMode("table");
    }
    fetchAopProgress(nextBreakdown);
  }

  function fetchPrograms() {
    if (!account_id) return;
    apiFetch(serverUrl + "/getProgram", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setPrograms(Array.isArray(data) ? data : []))
      .catch(() => setPrograms([]));
  }

  function buildActualDrillPath({
    programName,
    period,
    isTotalRow = false,
    breakdownOverride,
  }) {
    return buildAopActualDrillPathForCell({
      programName,
      breakdown: breakdownOverride || aopProgressBreakdown,
      fyStartDate: currentFyStartDate,
      asOfDate: asOfDateYmd,
      period,
      programs,
      isTotalRow,
    });
  }

  function fetchOperatingPlanSettings() {
    if (!account_id) return;
    apiFetch(serverUrl + "/getOperatingPlanSettings", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => {
        const date = ((data && data.current_fy_start_date) || "").trim();
        if (date && dayjs(date, "YYYY-MM-DD", true).isValid()) {
          setCurrentFyStartDate(date);
        }
        const fy = canonicalizeFiscalYear(((data && data.current_fy) || "").trim());
        if (fy) setCurrentFy(fy);
        fetchAopProgress();
      })
      .catch(() => {});
  }

  useEffect(() => {
    fetchPrograms();
    fetchOperatingPlanSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const todayDate = dayjs().format("MM/DD/YYYY");
  const fyStartParsed =
    currentFyStartDate && dayjs(currentFyStartDate, "YYYY-MM-DD", true);
  const daysElapsed =
    fyStartParsed && fyStartParsed.isValid()
      ? Math.max(0, dayjs().diff(fyStartParsed, "day"))
      : null;
  const pctFyElapsed =
    daysElapsed != null
      ? Math.min(100, Math.round((daysElapsed / 365) * 1000) / 10)
      : null;

  const aopProgramsForFilter = React.useMemo(
    () =>
      [
        ...new Set(
          aopProgressData.rows
            .map((r) => (r.program || "").trim())
            .filter(Boolean)
        ),
      ].sort(),
    [aopProgressData.rows]
  );

  const aopProgressDisplay = React.useMemo(() => {
    if (!aopProgressProgramFilter) return aopProgressData;
    const rows = aopProgressData.rows.filter(
      (r) => (r.program || "").trim() === aopProgressProgramFilter
    );
    const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;
    const actualPlanPct = (actual, plan) =>
      plan > 0 ? Math.round((actual / plan) * 10000) / 100 : null;
    if (rows.length === 0) {
      return {
        ...aopProgressData,
        rows: [],
        totalYearPlan: 0,
        totalYtdPlan: 0,
        totalYtdPlanPct: null,
        totalYtdActual: 0,
        totalYtdActualEntered: 0,
        totalYtdActualEvents: 0,
        totalYtdActualPlanPct: null,
        totalPeriods: [],
      };
    }
    const totalYearPlan = round2(
      rows.reduce((s, r) => s + (Number(r.yearPlan) || 0), 0)
    );
    if (aopProgressData.breakdown === BREAKDOWN_SUMMARY) {
      const totalYtdPlan = round2(
        rows.reduce((s, r) => s + (Number(r.ytdPlan) || 0), 0)
      );
      const totalYtdActual = round2(
        rows.reduce((s, r) => s + (Number(r.ytdActual) || 0), 0)
      );
      const totalYtdActualEntered = round2(
        rows.reduce((s, r) => s + (Number(r.ytdActualEntered) || 0), 0)
      );
      const totalYtdActualEvents = round2(
        rows.reduce((s, r) => s + (Number(r.ytdActualEvents) || 0), 0)
      );
      return {
        ...aopProgressData,
        rows,
        totalYearPlan,
        totalYtdPlan,
        totalYtdActual,
        totalYtdActualEntered,
        totalYtdActualEvents,
        totalYtdActualPlanPct: actualPlanPct(totalYtdActual, totalYtdPlan),
      };
    }
    const periodCount = Math.max(
      0,
      ...rows.map((r) => (r.periods || []).length)
    );
    const totalPeriods = Array.from({ length: periodCount }, (_, idx) => {
      const plan = round2(
        rows.reduce((s, r) => s + (Number(r.periods?.[idx]?.plan) || 0), 0)
      );
      const actual = round2(
        rows.reduce((s, r) => s + (Number(r.periods?.[idx]?.actual) || 0), 0)
      );
      return { plan, actual, actualPlanPct: actualPlanPct(actual, plan) };
    });
    return {
      ...aopProgressData,
      rows,
      totalYearPlan,
      totalPeriods,
    };
  }, [aopProgressData, aopProgressProgramFilter]);

  const ytdPlanFactor =
    aopProgressData.daysElapsed > 0 ? aopProgressData.daysElapsed / 365 : 0;

  return (
    <div className="page-container page-container--section-tabs page-container--ops-plan">
      <SectionTabsPageHeader>
        <OpsPlanSectionTabs activeTab={OPS_PLAN_TAB_DASHBOARD} />
      </SectionTabsPageHeader>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <div style={{ marginBottom: "8px" }}>
            <h1 className="page-title">Ops Plan Dashboard</h1>
            <p className="page-subtitle">{OPS_PLAN_AOP_PROGRESS_SUBTITLE}</p>
          </div>
          <div
            className="ops-plan-dashboard-inner-tabs"
            style={{
              display: "inline-flex",
              borderBottom: "1px solid #dee2e6",
              gap: "4px",
            }}
          >
            <button
              type="button"
              onClick={() => setActiveTab("current")}
              style={{
                padding: "6px 12px",
                fontSize: "13px",
                border: "none",
                borderBottom:
                  activeTab === "current"
                    ? "3px solid #0d6efd"
                    : "3px solid transparent",
                background: "transparent",
                color: activeTab === "current" ? "#0d6efd" : "#495057",
                fontWeight: activeTab === "current" ? 600 : 500,
                cursor: "pointer",
              }}
            >
              Current AOP Progress
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("programs")}
              style={{
                padding: "6px 12px",
                fontSize: "13px",
                border: "none",
                borderBottom:
                  activeTab === "programs"
                    ? "3px solid #0d6efd"
                    : "3px solid transparent",
                background: "transparent",
                color: activeTab === "programs" ? "#0d6efd" : "#495057",
                fontWeight: activeTab === "programs" ? 600 : 500,
                cursor: "pointer",
              }}
            >
              Programs
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("outcomes")}
              style={{
                padding: "6px 12px",
                fontSize: "13px",
                border: "none",
                borderBottom:
                  activeTab === "outcomes"
                    ? "3px solid #0d6efd"
                    : "3px solid transparent",
                background: "transparent",
                color: activeTab === "outcomes" ? "#0d6efd" : "#495057",
                fontWeight: activeTab === "outcomes" ? 600 : 500,
                cursor: "pointer",
              }}
            >
              Outcomes
            </button>
          </div>
        </div>

        {activeTab === "current" && (
          <>
            <OperatingPlanFyStatsRow
              currentFy={currentFy}
              currentFyStartDate={currentFyStartDate}
              todayDate={todayDate}
              daysElapsed={daysElapsed}
              pctFyElapsed={pctFyElapsed}
              onRefresh={fetchAopProgress}
              showRefresh
            />

            <div
              className="toolbar ops-plan-fy-stats-toolbar"
              style={{
                marginBottom: "12px",
                gap: "16px",
                padding: "0 0 8px",
              }}
            >
              <select
                value={aopProgressProgramFilter}
                onChange={(e) => setAopProgressProgramFilter(e.target.value)}
                className="input input-min-120"
                title="Filter by Program"
              >
                <option value="">All Programs</option>
                {aopProgramsForFilter.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <AopControlGroup label="Breakdown">
                <label style={aopRadioLabelStyle}>
                  <input
                    type="radio"
                    name="aopProgressBreakdown"
                    checked={aopProgressBreakdown === BREAKDOWN_SUMMARY}
                    onChange={() => handleBreakdownChange(BREAKDOWN_SUMMARY)}
                  />
                  Summary
                </label>
                <label style={aopRadioLabelStyle}>
                  <input
                    type="radio"
                    name="aopProgressBreakdown"
                    checked={aopProgressBreakdown === BREAKDOWN_WEEKLY}
                    onChange={() => handleBreakdownChange(BREAKDOWN_WEEKLY)}
                  />
                  YTD Weekly
                </label>
                <label style={aopRadioLabelStyle}>
                  <input
                    type="radio"
                    name="aopProgressBreakdown"
                    checked={aopProgressBreakdown === BREAKDOWN_MONTHLY}
                    onChange={() => handleBreakdownChange(BREAKDOWN_MONTHLY)}
                  />
                  YTD Monthly
                </label>
              </AopControlGroup>
              <AopControlGroup label="View">
                <label style={aopRadioLabelStyle}>
                  <input
                    type="radio"
                    name="aopProgressView"
                    checked={aopProgressViewMode === "table"}
                    onChange={() => setAopProgressViewMode("table")}
                  />
                  Table
                </label>
                <label
                  style={{
                    ...aopRadioLabelStyle,
                    cursor: aopProgressBreakdown === BREAKDOWN_SUMMARY
                      ? "pointer"
                      : "not-allowed",
                    opacity: aopProgressBreakdown === BREAKDOWN_SUMMARY ? 1 : 0.5,
                  }}
                >
                  <input
                    type="radio"
                    name="aopProgressView"
                    checked={aopProgressViewMode === "chart"}
                    disabled={aopProgressBreakdown !== BREAKDOWN_SUMMARY}
                    onChange={() => setAopProgressViewMode("chart")}
                  />
                  Graph
                </label>
              </AopControlGroup>
            </div>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
              }}
            >
              <div style={{ padding: "16px" }}>
                {aopProgressDisplay.rows.length > 0 ? (
                  aopProgressBreakdown === BREAKDOWN_SUMMARY &&
                  aopProgressViewMode === "chart" ? (
                    <AopProgressBarChart
                      rows={aopProgressDisplay.rows}
                      totalYtdPlan={aopProgressDisplay.totalYtdPlan}
                      totalYtdActual={aopProgressDisplay.totalYtdActual}
                    />
                  ) : aopProgressBreakdown !== BREAKDOWN_SUMMARY &&
                    aopProgressDisplay.periods.length === 0 ? (
                    <div
                      style={{
                        padding: "24px",
                        color: "#6c757d",
                        fontSize: "13px",
                      }}
                    >
                      Set Current FY Start Date above to view{" "}
                      {aopProgressBreakdown === BREAKDOWN_WEEKLY
                        ? "weekly"
                        : "monthly"}{" "}
                      breakdown.
                    </div>
                  ) : (
                    <AopProgressDataTable
                      breakdown={aopProgressBreakdown}
                      periods={aopProgressDisplay.periods}
                      rows={aopProgressDisplay.rows}
                      totalYearPlan={aopProgressDisplay.totalYearPlan}
                      totalPeriods={aopProgressDisplay.totalPeriods}
                      ytdPlanFactor={ytdPlanFactor}
                      buildActualDrillPath={buildActualDrillPath}
                      showLegend={aopProgressBreakdown === BREAKDOWN_SUMMARY}
                    />
                  )
                ) : (
                  <div
                    style={{
                      padding: "24px",
                      color: "#6c757d",
                      fontSize: "13px",
                    }}
                  >
                    No SOP records for Current FY. Use the Strategic Operating
                    Plan tab in Operating Plan to add data, or change Current FY
                    above.
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === "programs" && (
          <div
            style={{
              flex: 1,
              marginTop: "16px",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
              padding: "16px",
              backgroundColor: "#ffffff",
              overflow: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                alignItems: "stretch",
              }}
            >
              <div
                style={{
                  display: "block",
                  maxWidth: "960px",
                  margin: "0",
                }}
              >
                <img
                  src="/top-achievers-programs.jpg"
                  alt="Top Achievers programs and outcomes overview"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "calc(100vh - 220px)",
                    borderRadius: "6px",
                    boxShadow:
                      "0 2px 6px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(15, 23, 42, 0.08)",
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === "outcomes" && (
          <div
            style={{
              flex: 1,
              marginTop: "16px",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
              padding: "16px",
              backgroundColor: "#ffffff",
              overflow: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "24px",
                alignItems: "stretch",
              }}
            >
              <div
                style={{
                  display: "block",
                  maxWidth: "960px",
                  margin: "0",
                }}
              >
                <img
                  src="/top-achievers-outcomes.jpg"
                  alt="Top Achievers outcomes table"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "calc(100vh - 220px)",
                    borderRadius: "6px",
                    boxShadow:
                      "0 2px 6px rgba(15, 23, 42, 0.12), 0 1px 3px rgba(15, 23, 42, 0.08)",
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OpsPlanDashboard;
